import React, { useState, useEffect, useMemo, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import * as BBSFunctions from "../../../functions/Functions";
import { formatDateWbar } from "../../../components/Util/DateFormat";
import * as detailsDateFormat from "../../../components/Util/DateFormat";
import CommunityDetailPopup from "./Popup/CommunityDetailPopup";
import Swal from "sweetalert2";
import axios from "axios";
import { getServerBaseUrl } from "../../../utils/portConfig";
import styles from "./Community.module.css";
import DoctorSettingPopup from "../Users/Popup/DoctorSettingPopup";

function Community() {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [keyword, setKeyword] = useState("");
  const [commentList, setCommentList] = useState([]);
  const [showCommunity, setShowCommunityPopup] = useState(false);
  const [popupData, setPopupData] = useState(null);
  const [settingOpen, setSettingOpen] = useState(false);

  // 테이블 높이 설정값
  const [gridApi, setGridApi] = useState(null);
  const [pageSize, setPageSize] = useState();
  const gridContainerRef = useRef(null);

  const gridRef = useRef(null);

  const API_SERVER_V2_URL = `${getServerBaseUrl()}/api/v2`; // API 서버 URL
  const token = localStorage.getItem("token"); // 토큰
  const userId = localStorage.getItem("userid");

  useEffect(() => {
    getCommunityCategories();
  }, []);

  const resizeTimeoutRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        calculatePageSize();
      }, 300);
    };

    window.addEventListener("resize", handleResize);

    // 최초 1회 실행
    calculatePageSize();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [gridContainerRef]);

  useEffect(() => {
    if (gridApi && pageSize) {
      const dataSource = {
        getRows: async (params) => {
          await getData(params);
        },
      };
      gridApi.setDatasource(dataSource);

      // 페이지 사이즈가 바뀌면 캐시 초기화 및 첫 페이지로 이동
      // gridApi.purgeInfiniteCache();
      // gridApi.paginationGoToFirstPage();
    }
  }, [gridApi, pageSize, selectedCategory]);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const refreshGrid = () => {
    if (gridApi) {
      gridApi.refreshInfiniteCache();
    }
  };

  const getData = async (params) => {
    const page = Math.floor(params.startRow / pageSize);
    try {
      Swal.fire({
        title: "데이터를 가져오는 중...",
        text: "잠시만 기다려 주세요.",
        didOpen: () => {
          Swal.showLoading();
        },
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
      });

      const response = await axios.post(
        `${API_SERVER_V2_URL}/community/list`,
        {
          page: page, // 0부터 시작
          size: pageSize,
          keyword: keyword || null,
          sort: {
            createdDt: "desc",
          },
          filters: {
            category: selectedCategory || null,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data.data.content;
      const count = response.data.data.count;
      // const lastRow = count < params.endRow ? count : -1;
      params.successCallback(data, count);
      if (data.length === 0) {
        gridApi.showNoRowsOverlay();
      } else {
        gridApi.hideOverlay();
      }
    } catch (error) {
      console.error("Data fetch error:", error);
      Swal.fire({
        title: "오류!",
        text: "데이터를 가져오는 중 오류가 발생했습니다.",
        icon: "error",
        showConfirmButton: true,
      });
      params.successCallback([], 0);
    } finally {
      Swal.close();
    }
  };

  const getCommunityCategories = async () => {
    // 데이터 로딩 인디케이터 표시
    Swal.fire({
      title: "데이터를 가져오는 중...",
      text: "잠시만 기다려 주세요.",
      didOpen: () => {
        Swal.showLoading();
      },
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
    });
    try {
      const response = await axios.get(`${API_SERVER_V2_URL}/community/categories`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data.data;
      if (data.length > 0) {
        setCategories(data);
      }
    } catch (error) {
      Swal.fire({
        title: "오류!",
        text: "카테고리를 가져오는 중 오류가 발생했습니다.",
        icon: "error",
        showConfirmButton: true,
      });
      console.error("카테고리 데이터 가져오기 실패:", error);
    } finally {
      Swal.close();
    }
  };

  const prevPageRef = useRef(0);

  const onPaginationChanged = () => {
    if (gridRef.current && gridRef.current.api) {
      const currentPage = gridRef.current.api.paginationGetCurrentPage();
      if (prevPageRef.current !== currentPage) {
        prevPageRef.current = currentPage;
      }
    }
  };

  // 그리드 컬럼 정의
  const columns = [
    {
      field: "id",
      headerName: "번호",
      width: 130,
      suppressMenu: true,
    },
    {
      field: "title",
      headerName: "제목",
      cellStyle: { textAlign: "left", cursor: "pointer" },
      cellRenderer: cellRenderer,
      flex: 1,
    },
    {
      field: "nickname",
      headerName: "작성자",
      valueFormatter: (params) => params.data?.nickname || "탈퇴한 회원",
      width: 130,
    },
    {
      field: "category",
      headerName: "카테고리",
      valueFormatter: (params) => getCategoryName(params.data?.category),
      width: 130,
    },
    {
      field: "createdDt",
      headerName: "작성일",
      valueFormatter: (params) => formatDateWbar(params.data?.createdDt),
      width: 130,
    },
  ];

  const getCategoryName = (categoryName) => {
    if (categories.length === 0) return "기타";
    if (!categoryName) return "기타";

    const category = categories.find((cat) => cat.key === categoryName);
    return category?.name ? category.name : "기타";
  };

  // 💡 AG Grid 옵션에 cacheBlockSize를 추가
  const gridOptions = useMemo(
    () => ({
      // cacheBlockSize: pageSize,
      maxBlocksInCache: 1, // 필요에 따라 설정, 기본값 1
      // ... 다른 옵션들을 여기에 추가 가능
    }),
    [pageSize]
  );

  // 그리드 옵션
  const defaultColDef = useMemo(() => {
    return {
      sortable: false,
      filter: false,
      resizable: true,
    };
  }, []);

  // tooltip 기능
  function cellRenderer(params) {
    return (
      <span className="title-cell" title={params.value}>
        {params.value}
        {params.data?.commentsCount > 0 ? ` (${params.data?.commentsCount})` : ""}
      </span>
    );
  }

  // 셀 클릭 이벤트 처리
  const onCellClicked = async (params) => {
    if (params.colDef.headerName === "제목") {
      try {
        const resData = await BBSFunctions.getPatientCommunityDetail(API_SERVER_V2_URL, token, params.data.id);
        if (!resData) {
          // 상세 정보를 가져오지 못한 경우 오류 메시지 표시
          Swal.fire({
            title: "오류!",
            text: "데이터를 가져오는 중 오류가 발생했습니다.",
            icon: "error",
            showConfirmButton: true, // 사용자가 확인 버튼을 눌러야 닫힘
          });
          return;
        }

        setPopupData({
          id: resData.id,
          title: resData.title,
          content: resData.content,
          category: getCategoryName(resData.category),
          privatePost: resData.privatePost,
          userId: resData.userId,
          nickname: resData.nickname,
          createdDt: detailsDateFormat.formatKoreanDateTime(resData.createdDt),
          modifiedDt: resData.modifiedDt,
          attachments: resData.attachments,
          comments: resData.comments,
          mine: resData.mine,
        }); // 상세 정보를 상태에 설정
        setCommentList(resData.comments);
        setShowCommunityPopup(true); // 팝업 표시
      } catch (error) {
        console.error("데이터 가져오기 오류:", error);
        // 오류 처리를 원하는 대로 수행할 수 있습니다.
      }
    }
  };
  // 테이블 높이 변경 함수
  const calculatePageSize = () => {
    if (gridContainerRef.current) {
      const gridHeight = gridContainerRef.current.clientHeight;
      const rowHeight = 43; // AG Grid의 기본 행 높이 (alpine 테마)
      const headerHeight = 45; // 헤더 높이
      const paginationHeight = 45; // 페이지네이션 아래 여백

      const availableRows = Math.floor((gridHeight - headerHeight - paginationHeight) / rowHeight);

      // 최소 5개, 계산된 값보다 작은 행 수로 설정
      const newPageSize = Math.max(5, availableRows);
      setPageSize(newPageSize);
    }
  };

  // Enter 키로 검색
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      keywordRefreshData();
    }
  };

  const keywordRefreshData = async () => {
    if (gridApi) {
      gridApi.setDatasource({
        getRows: async (params) => {
          await getData(params);
        },
      });
    }
  };

  const onCloseSetting = () => {
    setSettingOpen(false);
  };

  return (
    <div className="dashboard-content">
      <div className="dashboard-content-container">
        <div className="dashboard-content-header">
          <h2>환자 커뮤니티(Q&A)</h2>
        </div>
        <div className={styles["search-field-container"]}>
          <button style={{ marginRight: "auto" }} onClick={() => setSettingOpen(true)}>
            커뮤니티 설정
          </button>
          <div className={styles["search-field"]}>
            <label>분류</label>
            <select className={styles["dashboard-content-select"]} value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              <option value="">분류 선택</option>
              {categories.map((category, index) => (
                <option key={index} value={category.key}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles["search-field"]}>
            <label>키워드</label>
            <input className={styles["keyword-field"]} type="text" placeholder="키워드 입력" value={keyword} onChange={(e) => setKeyword(e.target.value)} onKeyPress={handleKeyPress} />
            <button className={styles["green-button"]} onClick={keywordRefreshData}>
              검색
            </button>
          </div>
        </div>
        {showCommunity && (
          <CommunityDetailPopup
            data={popupData}
            commentList={commentList}
            setCommentList={setCommentList}
            onClose={() => setShowCommunityPopup(false)}
            token={token}
            userId={userId}
            getData={refreshGrid}
            getCategoryName={getCategoryName}
            setSettingOpen={setSettingOpen}
          />
        )}

        <div ref={gridContainerRef} className="ag-theme-alpine" style={{ height: "100%", width: "100%", fontWeight: "400" }}>
          {pageSize && (
            <AgGridReact
              ref={gridRef}
              pagination={true}
              rowModelType={"infinite"}
              onGridReady={onGridReady}
              onCellClicked={onCellClicked}
              columnDefs={columns}
              cacheBlockSize={pageSize}
              paginationPageSize={pageSize}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              onPaginationChanged={onPaginationChanged}
              overlayNoRowsTemplate="<span class='ag-overlay-no-rows-center'>조회된 데이터가 없습니다.</span>"
            />
          )}
        </div>
      </div>
      <DoctorSettingPopup isOpen={settingOpen} setIsOpen={setSettingOpen} onClose={onCloseSetting} isPasswordInput={false} title="커뮤니티 설정" />
    </div>
  );
}

export default Community;
